import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api-config";
import { apiGet, apiRequest } from "@/utils/api-interceptor";
import { useAuth } from "@/context/AuthContext";

/** The kinds of add-on this build has labels for. */
export const KNOWN_ADDON_TYPES = [
  'AUTO_TICKET_RESPONSE', 'WHITE_LABELING', 'ADD_ON_AGENT', 'BACKUP_AGENT',
] as const;
export type KnownAddonType = (typeof KNOWN_ADDON_TYPES)[number];

/**
 * The API gains new add-on kinds without the admin being rebuilt, so an
 * unrecognised value is still a valid one — it is a kind we have no label for
 * yet, not bad data. Typing it as a closed union made every new kind read as
 * an empty field.
 */
export type AddonType = KnownAddonType | (string & {});

const ADDON_TYPE_LABELS: Record<KnownAddonType, { label: string; description: string }> = {
  AUTO_TICKET_RESPONSE: { label: 'Automatic ticket replies', description: 'Agents answer tickets without a person.' },
  WHITE_LABELING: { label: 'White labelling', description: 'The workspace’s own brand, not 7en’s.' },
  ADD_ON_AGENT: { label: 'Extra agent', description: 'One more agent beyond the plan’s allowance.' },
  BACKUP_AGENT: { label: 'Agent backup', description: 'Snapshots of an agent, restorable for 30 days.' },
};

/** Makes an unrecognised API value readable: BACKUP_AGENT → "Backup agent". */
export function readableAddonType(value: AddonType) {
  if (value in ADDON_TYPE_LABELS) return ADDON_TYPE_LABELS[value as KnownAddonType].label;
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : value;
}

/**
 * The choices to offer for `current`. An add-on whose kind this build has no
 * label for still gets an entry, so opening it never shows an empty field and
 * saving never quietly rewrites what the add-on is.
 */
export function addonTypeOptions(current?: AddonType) {
  const known = KNOWN_ADDON_TYPES.map((value) => ({ value: value as AddonType, ...ADDON_TYPE_LABELS[value] }));
  if (!current || known.some((option) => option.value === current)) return known;
  return [...known, {
    value: current,
    label: readableAddonType(current),
    description: 'A newer kind this build has no description for.',
  }];
}

export interface Addon {
  id: number;
  name: string;
  description: string;
  price_monthly: string;
  addon_type: AddonType;
  status: 'ACTIVE' | 'INACTIVE';
  created_at?: string;
  updated_at?: string;
}

export interface CreateAddonData {
  name: string;
  description: string;
  price_monthly: number;
  addon_type: AddonType;
  status: 'ACTIVE' | 'INACTIVE';
}

async function fetchAddons(): Promise<Addon[]> {
  console.log('Fetching addons from API...');
  const response = await apiGet(getApiUrl('subscriptions/addons/'));

  if (!response.ok) {
    console.error(`Addons API error: ${response.status}`);
    throw new Error('Failed to fetch addons');
  }

  const data = await response.json();
  console.log('Addons data received:', data);
  return data.data || data;
}

async function createAddon(addonData: CreateAddonData): Promise<Addon> {
  console.log('Creating addon:', addonData);
  const response = await apiRequest(getApiUrl('subscriptions/addons/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addonData)
  });

  if (!response.ok) {
    console.error(`Create addon API error: ${response.status}`);
    throw new Error('Failed to create addon');
  }

  const data = await response.json();
  console.log('Addon created:', data);
  return data.data;
}

async function updateAddon(id: number, addonData: Partial<CreateAddonData>): Promise<Addon> {
  console.log('Updating addon:', id, addonData);
  const response = await apiRequest(getApiUrl(`subscriptions/addons/${id}/`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addonData)
  });

  if (!response.ok) {
    console.error(`Update addon API error: ${response.status}`);
    throw new Error('Failed to update addon');
  }

  const data = await response.json();
  console.log('Addon updated:', data);
  return data.data;
}

async function deleteAddon(id: number): Promise<void> {
  console.log('Deleting addon:', id);
  const response = await apiRequest(getApiUrl(`subscriptions/addons/${id}/`), {
    method: 'DELETE'
  });

  if (!response.ok) {
    console.error(`Delete addon API error: ${response.status}`);
    throw new Error('Failed to delete addon');
  }
}

// Dummy data for when API is not connected
const dummyAddons: Addon[] = [
  {
    id: 1,
    name: "Auto Ticket Response",
    description: "Automatically respond to support tickets with AI-powered responses",
    price_monthly: "49.00",
    addon_type: 'AUTO_TICKET_RESPONSE',
    status: 'ACTIVE',
  },
  {
    id: 2,
    name: "White Label",
    description: "Remove branding and customize the platform with your own logo",
    price_monthly: "39.00",
    addon_type: 'WHITE_LABELING',
    status: 'ACTIVE',
  },
];

export function useAddons() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['addons'],
    queryFn: async () => {
      try {
        return await fetchAddons();
      } catch (error) {
        console.log('API not connected, using dummy data');
        return dummyAddons;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
    enabled: isAuthenticated,
  });
}

export function useCreateAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAddon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
    },
  });
}

export function useUpdateAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Partial<CreateAddonData>) =>
      updateAddon(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
    },
  });
}

export function useDeleteAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAddon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
    },
  });
}
